import React, { useEffect, useRef, useState } from 'react';
import { BsPlus } from 'react-icons/bs';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import TagProductBox from '../../components/Modals/TagProductBox';
import './ImageHotspot.scss';
import { getCloudflareWidth } from '../../utils/rounding';

export default function ImageHotspot(props) {
  const { getClickedHotspot = null, hotspots, src, id, style = {}, alt = '', layout, backgroundBlur = false } = props;
  const [open, setOpen] = React.useState(-1);
  const [resizedSrc, setResizedSrc] = useState(src);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const hotSpotid = `image-hotspot-${src}-${id}-${new Date().getMilliseconds()}`;

  // Cloudflare resize logic
  useEffect(() => {
    if (!imgRef.current) return;

    const updateSrc = (width: number) => {
      try {
        const parsed = new URL(src);
        if (parsed.hostname === 'idukki-cdn.com') {
          setResizedSrc(
            `${parsed.origin}/cdn-cgi/image/width=${getCloudflareWidth(width)},quality=90,format=auto${
              parsed.pathname
            }`,
          );
        } else {
          setResizedSrc(src);
        }
      } catch {
        setResizedSrc(src);
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateSrc(entry.contentRect.width);
      }
    });

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [src]);

  const getCords = (event) => {
    setOpen(-1);
    const ele = document.getElementById(hotSpotid);
    const topPos = ele.getBoundingClientRect().top + window.scrollY;
    const leftPos = ele.getBoundingClientRect().left + window.scrollX;
    const x = ((event.pageX - leftPos) / ele.offsetWidth) * 100;
    const y = ((event.pageY - topPos) / ele.offsetHeight) * 100;
    if (getClickedHotspot)
      getClickedHotspot({
        x: parseFloat(x.toFixed(1)),
        y: parseFloat(y.toFixed(1)),
      });
  };

  return (
    <idk-div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'white',
        overflow: 'hidden',
        ...style,
      }}
      onClick={getCords}
      class="circle-hotspot"
    >
      {backgroundBlur && (
        <img
          src={resizedSrc}
          className="zid-absolute zid-inset-0 zid-w-full zid-h-full zid-object-cover zid-blur-xl zid-scale-110 zid-z-0"
          alt={alt}
        />
      )}
      <img
        ref={imgRef}
        src={resizedSrc}
        alt={alt}
        style={{
          position: 'relative',
          zIndex: 1,
          objectFit: 'cover',
          width: '100%',
          height: '100%',
          ...style,
        }}
        loading="lazy"
        id={hotSpotid}
        onError={(e) => {
          const currentSrc = e.currentTarget.src;
          if (currentSrc.includes('idukki-cdn.com')) {
            e.currentTarget.onerror = null;
            e.currentTarget.src = currentSrc.replace('https://idukki-cdn.com', 'https://assets.idukki.io');
          } else {
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMTAwJyBoZWlnaHQ9JzEwMCcgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJz48cmVjdCB3aWR0aD0nMTAwJyBoZWlnaHQ9JzEwMCcgZmlsbD0nI2U2ZTZlNicvPjwvc3ZnPg==';
          }
        }}
      />
      {layout?.gallerySettings?.showHotspotProducts &&
        hotspots?.map((row: any) => {
          const hotspotId = row?.i ? row?.i : 0;
          const index = row?.id_from_product;
          if (hotspotId == id) {
            return (
              <idk-div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(open === -1 ? index : -1);
                }}
                onMouseEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(index);
                }}
                onMouseLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(-1);
                }}
                key={index}
              >
                <Popover
                  isOpen={open == index}
                  positions={['top', 'bottom', 'left', 'right']}
                  content={({ position, childRect, popoverRect }) => (
                    <ArrowContainer
                      position={position}
                      childRect={childRect}
                      popoverRect={popoverRect}
                      arrowColor={'#fff'}
                      arrowSize={6}
                      className="popover-arrow-container"
                      arrowClassName="popover-arrow"
                    >
                      <TagProductBox data={row.data} />
                    </ArrowContainer>
                  )}
                >
                  <idk-div
                    class="zid-flex zid-items-center zid-justify-center"
                    style={{
                      position: 'absolute',
                      top: `calc(${row.y}% - 9px)`,
                      left: `calc(${row.x}% - 9px)`,
                      zIndex: 10,
                      cursor: 'pointer',
                      width: '18px',
                      height: '18px',
                    }}
                  >
                    <idk-div class="pulsating-circle">
                      <BsPlus
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transition: 'all 0.4s',
                          transform:
                            open == index ? 'translate(-50%,-50%) rotate(120deg)' : 'translate(-50%,-50%) rotate(0deg)',
                          zIndex: 100,
                        }}
                      />
                    </idk-div>
                  </idk-div>
                </Popover>
              </idk-div>
            );
          }
          return null;
        })}
    </idk-div>
  );
}
